/** @format */

import React, { useEffect, useRef } from "react";
import {
  Search,
  X,
  Sofa,
  Package,
  FileText,
  Briefcase,
  Wrench,
} from "lucide-react";
import Link from "next/link";
import { FormatUtils } from "@/utils/format";
import { ImageLoader } from "../../../image-loader";

export interface SearchResult {
  name?: string;
  title?: string;
  slug: string;
  price?: number;
  image: { url: string } | null;
  _type: "product" | "article" | "project" | "service";
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: SearchResult[];
  isSearching: boolean;
}

export const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  searchQuery,
  setSearchQuery,
  searchResults,
  isSearching,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const getDisplayName = (result: SearchResult) =>
    result.name || result.title || "Untitled";

  const getLinkPath = (result: SearchResult) => {
    const pathMap = {
      product: "/san-pham",
      article: "/tin-tuc",
      project: "/du-an",
      service: "/dich-vu",
    };
    return `${pathMap[result._type]}/${result.slug}`;
  };

  const getTypeInfo = (type: SearchResult["_type"]) => {
    const typeMap = {
      product: { label: "Sản phẩm", icon: Package, color: "text-blue-600" },
      article: { label: "Bài viết", icon: FileText, color: "text-green-600" },
      project: { label: "Dự án", icon: Briefcase, color: "text-purple-600" },
      service: { label: "Dịch vụ", icon: Wrench, color: "text-orange-600" },
    };
    return (
      typeMap[type] || {
        label: "Khác",
        icon: Package,
        color: "text-muted-foreground",
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[70vh] overflow-hidden rounded-3xl bg-background border border-border shadow-xl animate-in fade-in zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted/40">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl border border-border">
              <Search className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">Tìm kiếm</h3>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-muted transition">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Input */}
        <div className="p-6 border-b border-border">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-primary" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Tìm kiếm sản phẩm, bài viết, dự án..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="
                w-full pl-14 pr-4 py-4 text-lg
                border border-border
                rounded-xl
                bg-background
                text-foreground
                focus:ring-2 focus:ring-ring
                focus:border-primary
                transition
              "
            />
          </div>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {isSearching && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin h-10 w-10 rounded-full border-4 border-primary border-t-transparent mb-4" />
              <p className="text-primary font-medium">Đang tìm kiếm...</p>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && (
            <div className="p-6 space-y-4">
              <p className="text-sm font-semibold text-primary bg-muted px-3 py-2 rounded-lg border border-border">
                Tìm thấy {searchResults.length} kết quả
              </p>

              {searchResults.map((result, index) => {
                if (!result?._type || !result.slug) return null;

                const typeInfo = getTypeInfo(result._type);
                const TypeIcon = typeInfo.icon;

                return (
                  <Link
                    key={`${result._type}-${result.slug}-${index}`}
                    href={getLinkPath(result)}
                    onClick={onClose}
                    className="
                      group flex items-center gap-4 p-4
                      rounded-xl
                      hover:bg-muted/50
                      border border-transparent
                      hover:border-border
                      transition
                    ">
                    {/* Image */}
                    <div className="relative w-16 h-16 flex-shrink-0">
                      {result.image?.url ? (
                        <ImageLoader
                          src={result.image.url}
                          alt={getDisplayName(result)}
                          fill
                          className="rounded-xl object-cover border border-border"
                        />
                      ) : (
                        <div className="w-full h-full rounded-xl bg-muted flex items-center justify-center border border-border">
                          <TypeIcon className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                        <span
                          className={`text-xs font-medium ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                      </div>

                      <h4 className="text-sm font-semibold text-foreground group-hover:text-primary truncate">
                        {getDisplayName(result)}
                      </h4>

                      {result.price && (
                        <div className="block sm:hidden text-sm font-bold text-primary mt-1">
                          {FormatUtils.formatPriceVND(result.price)}
                        </div>
                      )}
                    </div>

                    {result.price && (
                      <div className="hidden sm:block text-right text-lg font-bold text-primary">
                        {FormatUtils.formatPriceVND(result.price)}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}

          {!isSearching && searchQuery && searchResults.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Sofa className="h-10 w-10 text-primary" />
              </div>
              <p className="text-lg font-bold text-foreground mb-2">
                Không tìm thấy kết quả
              </p>
              <p className="text-muted-foreground text-center px-4">
                Không có kết quả nào phù hợp với "{searchQuery}"
              </p>
            </div>
          )}

          {!searchQuery && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Search className="h-10 w-10 text-primary" />
              </div>
              <p className="text-lg font-bold text-foreground mb-2">
                Khám phá nội dung
              </p>
              <p className="text-muted-foreground text-center px-4">
                Nhập từ khóa để tìm kiếm sản phẩm, bài viết, dự án hoặc dịch vụ
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
